import * as React from "react";
import * as ReactDOM from "react-dom";
import clsx from "clsx";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Settings, Info, CheckCircle2, AlertCircle, Hourglass, Lock, PenLine, Save, RotateCcw, Braces, WifiOff, HelpCircle, Copy, Check, Battery, Zap, Sun, Fuel, Clock, Wifi, SlidersHorizontal, Gauge } from "lucide-react";
import { JsonView, darkStyles } from "react-json-view-lite";
import "react-json-view-lite/dist/index.css";
import * as riStyles from "../../../ui/components/RealtimeIndicator/RealtimeIndicator.style.css";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import {
  getProductById,
  fetchProductSettingShadowNames,
  fetchProductShadowSettings,
  fetchProductShadow,
  fetchTelemetryLatest,
  isBluetoothOnlyLine,
  updateProductShadowSettings,
  type Product,
  type EnrichedSetting,
  type ShadowPipelineResult,
  type ShadowState,
} from "../../lib/api/products";
import * as productAccessApi from "../../lib/api/product-access";
import { useWebSocket } from "../../hooks/useWebSocket";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import {
  productSettingsCollapseScope,
  useSectionCollapse,
} from "../../hooks/useSectionCollapse";
import { Text } from "../../../ui/components/Text";
import { Callout } from "../../../ui/components/Callout";
import { Modal } from "../../../ui/components/Modal";
import { TabSwitcher } from "../../../ui/components/TabSwitcher";
import { CollapsibleSection } from "../../../ui/components/CollapsibleSection";
import { UnsavedChangesChip, scrollToFirstUnsavedInSection } from "../../../ui/components/UnsavedChangesChip";
import type { FloatingAction } from "../../../ui/components/FloatingActionBar";
import { DetailPageLayout } from "../layouts/DetailPageLayout";
import { Button } from "../../../ui/components/Button";
import * as s from "./ProductSettingsPage.css";
import { ComingSoonCard } from "../../../ui/components/ComingSoonCard";

// ─── Shadow sync status helpers ───────────────────────────────────────────────

// Shadow data is event-driven: devices only push updates when something changes,
// so staleness of the shadow document is NOT a reliable offline signal.
// Status is derived from the shadow delta state instead.
type ShadowSyncStatus = 'synced' | 'pending' | 'neverSynced' | 'unknown';

function deriveShadowSyncStatus(settings: EnrichedSetting[] | null | undefined): ShadowSyncStatus {
  if (!settings || settings.length === 0) return 'unknown';
  const hasReported = settings.some((s) => s.reportedValue != null);
  if (!hasReported) return 'neverSynced';
  const hasPending = settings.some((s) => s.status === 'pending');
  return hasPending ? 'pending' : 'synced';
}

function formatSyncAge(lastSyncMs: number): string {
  const ageSec = Math.floor((Date.now() - lastSyncMs) / 1000);
  if (ageSec < 60) return `${ageSec}s ago`;
  const ageMin = Math.floor(ageSec / 60);
  if (ageMin < 60) return `${ageMin} min${ageMin === 1 ? "" : "s"} ago`;
  const ageHrs = Math.floor(ageMin / 60);
  if (ageHrs < 24) return `${ageHrs} hr${ageHrs === 1 ? "" : "s"} ago`;
  return `${Math.floor(ageHrs / 24)} day${Math.floor(ageHrs / 24) === 1 ? "" : "s"} ago`;
}

interface ProductSettingsPageProps {
  productId: string;
}

type FieldStatus = "synced" | "unsaved" | "pending" | "error" | "locked";

function deriveStatus(
  setting: EnrichedSetting,
  localValue: string | undefined,
  fieldError: string | undefined,
): FieldStatus {
  if (!setting.isEditable) return "locked";
  if (fieldError) return "error";
  // A local override takes priority over pending — this is how the user reverts a pending change.
  if (localValue !== undefined) return "unsaved";
  if (setting.status === "pending" || setting.status === "unconfirmed") return "pending";
  return "synced";
}

// ─── Tooltip wrapper ──────────────────────────────────────────────────────────

function TooltipWrap({ lines, children }: { lines: string[]; children: React.ReactNode }) {
  const [visible, setVisible] = React.useState(false);
  return (
    <div
      className={s.statusIconWrap}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      {visible && (
        <div className={s.tooltip}>
          {lines.map((line, i) => <div key={i}>{line}</div>)}
        </div>
      )}
    </div>
  );
}

// ─── Status icon badge ────────────────────────────────────────────────────────

interface StatusIconProps {
  status: FieldStatus;
  error?: string;
  reportedValue?: string | null;
  desiredValue?: string;
  unit?: string | null;
  lastSyncAt?: number;
  deviceLastSync?: number;
  pendingSince?: number;
}

function StatusIcon({ status, error, reportedValue, desiredValue, unit, lastSyncAt, deviceLastSync, pendingSince }: StatusIconProps) {
  const tooltipLines: string[] = [];
  let icon: React.ReactNode;
  let colorClass: string;

  const u = unit ? ` ${unit}` : "";

  switch (status) {
    case "synced":
      if (lastSyncAt != null) {
        tooltipLines.push(`Synced ${relativeTime(lastSyncAt)}`);
      } else {
        tooltipLines.push("Synced");
      }
      icon = <CheckCircle2 size={18} />;
      colorClass = s.statusSynced;
      break;
    case "unsaved":
      tooltipLines.push("Unsaved changes");
      tooltipLines.push("Save to apply");
      icon = <PenLine size={18} />;
      colorClass = s.statusUnsaved;
      break;
    case "pending":
      tooltipLines.push("Pending");
      if (reportedValue != null && desiredValue != null) {
        tooltipLines.push(`${reportedValue}${u} → ${desiredValue}${u}`);
      }
      tooltipLines.push("Waiting for device confirmation");
      if (pendingSince != null) {
        tooltipLines.push(`Change requested ${formatSyncAge(pendingSince)}`);
      }
      if (deviceLastSync != null) {
        tooltipLines.push(`Last device sync: ${formatSyncAge(deviceLastSync)}`);
      }
      icon = <Hourglass size={18} className={s.pulsingIcon} />;
      colorClass = s.statusPending;
      break;
    case "error":
      if (error) {
        tooltipLines.push("Save failed");
        tooltipLines.push(error);
      } else {
        tooltipLines.push("Save failed");
        if (deviceLastSync != null) {
          tooltipLines.push(`Last device sync: ${formatSyncAge(deviceLastSync)}`);
        }
      }
      icon = <AlertCircle size={18} />;
      colorClass = s.statusError;
      break;
    case "locked":
      tooltipLines.push("Read-only");
      tooltipLines.push("This setting cannot be edited");
      icon = <Lock size={18} />;
      colorClass = s.statusLocked;
      break;
  }

  return (
    <TooltipWrap lines={tooltipLines}>
      <div className={clsx(s.statusIcon, colorClass)}>{icon}</div>
    </TooltipWrap>
  );
}

// ─── Description modal ────────────────────────────────────────────────────────

function DescriptionModal({ setting, onClose }: { setting: EnrichedSetting; onClose: () => void }) {
  const { t, tx } = useI18n();
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className={s.descOverlay} onClick={onClose}>
      <div className={s.descCard} onClick={(e) => e.stopPropagation()}>
        <div className={s.descTitle}>{setting.label}</div>
        <span className={s.descKeyRow}>{setting.key}</span>
        {setting.description && (
          <div className={s.descBody}>{setting.description}</div>
        )}
        {setting.enumOptions && setting.enumOptions.length > 0 && (
          <div className={s.descBody}>
            <strong>{tx("pro.productSettings.options")}</strong>
            <ul style={{ margin: "4px 0 0 0", paddingLeft: 18 }}>
              {setting.enumOptions.map((opt) => (
                <li key={opt.value} style={{ marginBottom: 2 }}>
                  <code style={{ fontSize: 11 }}>{opt.value}</code> — {opt.label}
                </li>
              ))}
            </ul>
          </div>
        )}
        <button className={s.descCloseBtn} onClick={onClose}>{tx("shared.languagePicker.close")}</button>
      </div>
    </div>
  );
}

// ─── Relative time formatter ──────────────────────────────────────────────────

function relativeTime(ms: number): string {
  const diff = Math.floor((Date.now() - ms) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ─── Individual setting row ───────────────────────────────────────────────────

const SETTING_ROW_COMMIT_MS = 200;

interface SettingRowProps {
  setting: EnrichedSetting;
  localValue: string | undefined;
  fieldError: string | undefined;
  onLocalChange: (key: string, value: string, reportedValue?: string | null, forceSet?: boolean) => void;
  flashKey: number;
  deviceLastSync?: number;
  pendingSince?: number;
}

const SettingRow = React.memo(function SettingRow({
  setting,
  localValue,
  fieldError,
  onLocalChange,
  flashKey,
  deviceLastSync,
  pendingSince,
}: SettingRowProps) {
  const { t, tx } = useI18n();
  const [descOpen, setDescOpen] = React.useState(false);
  const committedDisplay = localValue ?? setting.reportedValue ?? "";
  const [draftValue, setDraftValue] = React.useState(committedDisplay);
  const [focused, setFocused] = React.useState(false);
  const onLocalChangeRef = React.useRef(onLocalChange);
  onLocalChangeRef.current = onLocalChange;
  const draftValueRef = React.useRef(draftValue);
  draftValueRef.current = draftValue;
  const committedRef = React.useRef(committedDisplay);
  committedRef.current = committedDisplay;
  const settingRef = React.useRef(setting);
  settingRef.current = setting;

  React.useEffect(() => {
    if (focused) return;
    setDraftValue(committedDisplay);
  }, [committedDisplay, focused]);

  const flush = React.useCallback(() => {
    const next = draftValueRef.current;
    const committed = committedRef.current;
    if (next === committed) return;
    onLocalChangeRef.current(
      settingRef.current.key,
      next,
      settingRef.current.reportedValue,
    );
  }, []);

  React.useEffect(() => {
    if (!focused) return;
    if (draftValue === committedDisplay) return;
    const timer = window.setTimeout(flush, SETTING_ROW_COMMIT_MS);
    return () => window.clearTimeout(timer);
  }, [draftValue, focused, committedDisplay, flush]);

  React.useEffect(() => () => flush(), [flush]);

  const reported = setting.reportedValue ?? "";
  // While focused, status follows the live draft so tint tracks keystrokes without
  // waiting for the debounced parent commit. forceSet (pending revert) keeps unsaved
  // even when draft === reported.
  const statusLocalValue: string | undefined = focused
    ? draftValue !== reported
      ? draftValue
      : localValue !== undefined
        ? localValue
        : undefined
    : localValue;

  const fieldStatus = deriveStatus(setting, statusLocalValue, fieldError);
  const effectiveError = fieldError;
  const isDisabled = fieldStatus === "pending" || fieldStatus === "locked";
  const controlId = `setting-${setting.key}`;
  const displayValue =
    focused || draftValue !== committedDisplay ? draftValue : committedDisplay;

  const tintClass =
    fieldStatus === "unsaved" ? s.inputTintUnsaved :
    fieldStatus === "pending" ? s.inputTintPending :
    fieldStatus === "error"   ? s.inputTintError :
    fieldStatus === "locked"  ? s.inputTintLocked :
    undefined;

  const commitImmediate = (value: string, forceSet?: boolean) => {
    setDraftValue(value);
    onLocalChange(setting.key, value, setting.reportedValue, forceSet);
  };

  function renderInput() {
    if (setting.valueType === "enum" && setting.enumOptions && setting.enumOptions.length > 0) {
      return (
        <select
          key={flashKey}
          id={controlId}
          className={clsx(s.settingSelect, tintClass, flashKey > 0 && s.flashHighlight)}
          value={displayValue}
          disabled={isDisabled}
          onChange={(e) => commitImmediate(e.target.value)}
        >
          {displayValue === "" && <option value="">— Select —</option>}
          {setting.enumOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      );
    }

    if (setting.valueType === "boolean") {
      return (
        <select
          key={flashKey}
          id={controlId}
          className={clsx(s.settingSelect, tintClass, flashKey > 0 && s.flashHighlight)}
          value={displayValue}
          disabled={isDisabled}
          onChange={(e) => commitImmediate(e.target.value)}
        >
          <option value="1">{t("pro.productSettings.on")}</option>
          <option value="0">{t("pro.productSettings.off")}</option>
        </select>
      );
    }

    const isNumber = setting.valueType === "number";
    return (
      <div className={s.inputWrapper}>
        <input
          key={flashKey}
          id={controlId}
          className={clsx(
            s.settingInput,
            tintClass,
            flashKey > 0 && s.flashHighlight,
            setting.unit && (isNumber ? s.settingInputNumberWithUnit : s.settingInputWithUnit),
          )}
          type={isNumber ? "number" : "text"}
          step={setting.precision != null ? Math.pow(10, -setting.precision) : undefined}
          value={displayValue}
          disabled={isDisabled}
          onChange={(e) => setDraftValue(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            flush();
            setFocused(false);
          }}
        />
        {setting.unit && (
          <span className={clsx(s.inputUnit, isNumber && s.inputUnitForNumber)}>
            {setting.unit}
          </span>
        )}
      </div>
    );
  }

  const hasInfo = !!(setting.description || (setting.enumOptions && setting.enumOptions.length > 0));

  return (
    <>
      <div
        className={s.settingRow}
        data-unsaved={fieldStatus === "unsaved" ? "true" : undefined}
      >
        <div className={s.settingInfo}>
          <div className={s.settingLabelRow}>
            <label htmlFor={controlId} className={s.settingLabel}>{setting.label}</label>
            {hasInfo && (
              <button
                type="button"
                className={s.infoBtn}
                onClick={() => setDescOpen(true)}
                aria-label={`Info for ${setting.label}`}
              >
                <Info size={13} />
              </button>
            )}
          </div>
          <div className={s.settingMeta}>
            <span className={s.settingKey}>{setting.key}</span>
          </div>
        </div>

        <div className={s.controlArea}>
          <button
            type="button"
            className={s.resetBtn}
            style={{ visibility: (fieldStatus === "unsaved" || fieldStatus === "pending") ? "visible" : "hidden" }}
            onClick={() =>
              commitImmediate(
                setting.reportedValue ?? "",
                /* forceSet: only keep the edit when there's a real delta to clear */
                setting.status === "pending" || setting.status === "unconfirmed",
              )
            }
            title={fieldStatus === "pending" ? "Revert change request" : "Reset to reported value"}
          >
            <RotateCcw size={12} />
          </button>
          <div className={s.inputCell}>
            {renderInput()}
          </div>
          <StatusIcon
            status={fieldStatus}
            error={effectiveError}
            reportedValue={setting.reportedValue}
            desiredValue={setting.desiredValue}
            unit={setting.unit}
            lastSyncAt={setting.lastSyncAt}
            deviceLastSync={deviceLastSync}
            pendingSince={pendingSince}
          />
        </div>
      </div>

      {descOpen && (
        <DescriptionModal setting={setting} onClose={() => setDescOpen(false)} />
      )}
    </>
  );
});

// ─── Section helpers ──────────────────────────────────────────────────────────

function sectionToId(section: string): string {
  return `section-${section.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") || "general"}`;
}

function sectionIcon(section: string, size: number): React.ReactNode {
  const key = section.toLowerCase();
  if (!key || key === "general" || key.includes("system")) {
    return <Settings size={size} />;
  }
  if (key.includes("batter") || key.includes("bms") || key.includes("soc")) {
    return <Battery size={size} />;
  }
  if (key.includes("grid") || key.includes("ac ") || key.startsWith("ac")) {
    return <Zap size={size} />;
  }
  if (key.includes("solar") || key.includes("pv") || key.includes("inverter")) {
    return <Sun size={size} />;
  }
  if (key.includes("generator") || key.includes("gen ")) {
    return <Fuel size={size} />;
  }
  if (
    key.includes("time") ||
    key.includes("schedule") ||
    key.includes("tou") ||
    key.includes("work mode")
  ) {
    return <Clock size={size} />;
  }
  if (
    key.includes("network") ||
    key.includes("wifi") ||
    key.includes("comm") ||
    key.includes("modem")
  ) {
    return <Wifi size={size} />;
  }
  if (key.includes("meter") || key.includes("power") || key.includes("load")) {
    return <Gauge size={size} />;
  }
  return <SlidersHorizontal size={size} />;
}

// ─── Section card with grouped rows ──────────────────────────────────────────

interface SettingSectionProps {
  sectionName: string;
  settings: EnrichedSetting[];
  localEdits: Record<string, string>;
  fieldErrors: Record<string, string>;
  flashKeys: Record<string, number>;
  onLocalChange: (key: string, value: string, reportedValue?: string | null, forceSet?: boolean) => void;
  deviceLastSync?: number;
  pendingSince: Record<string, number>;
  expanded: boolean;
  onToggleExpanded: () => void;
}

const SettingSection = React.memo(function SettingSection({
  sectionName,
  settings,
  localEdits,
  fieldErrors,
  flashKeys,
  onLocalChange,
  deviceLastSync,
  pendingSince,
  expanded,
  onToggleExpanded,
}: SettingSectionProps) {
  const { t } = useI18n();
  const dirtyCount = settings.filter((s) => s.isEditable && s.key in localEdits).length;
  const sectionId = sectionToId(sectionName);
  const settingCount = settings.length;
  const title = sectionName || "General";

  return (
    <CollapsibleSection
      id={sectionId}
      title={title}
      subtitle={`${settingCount} setting${settingCount !== 1 ? "s" : ""}`}
      icon={sectionIcon(sectionName, 20)}
      expanded={expanded}
      onToggleExpanded={onToggleExpanded}
      highlight={dirtyCount > 0}
      mutedHeader
      compactHeader
      flushBody
      unmountCollapsed
      statusIndicators={
        dirtyCount > 0
          ? [
              <UnsavedChangesChip
                key="u"
                count={dirtyCount}
                title={t("pro.productSettings.scrollToFirstUnsaved")}
                onClick={() =>
                  scrollToFirstUnsavedInSection(sectionId, {
                    expanded,
                    onExpand: onToggleExpanded,
                  })
                }
              />,
            ]
          : undefined
      }
    >
      {settings.map((setting) => (
        <SettingRow
          key={setting.key}
          setting={setting}
          localValue={localEdits[setting.key]}
          fieldError={fieldErrors[setting.key]}
          onLocalChange={onLocalChange}
          flashKey={flashKeys[setting.key] ?? 0}
          deviceLastSync={deviceLastSync}
          pendingSince={pendingSince[setting.key]}
        />
      ))}
    </CollapsibleSection>
  );
});

// ─── Shadow JSON modal ────────────────────────────────────────────────────────

// ─── Shadow sync indicator ────────────────────────────────────────────────────

const SHADOW_STATUS_LABEL: Record<ShadowSyncStatus, string> = {
  synced:     'Settings: Synced',
  pending:    'Settings: Pending',
  neverSynced:'Settings: Not reported',
  unknown:    'Settings: Unknown',
};

const SHADOW_STATUS_DESC: Record<ShadowSyncStatus, string> = {
  synced:     'All settings are confirmed by the device.',
  pending:    'One or more settings are awaiting confirmation from the device.',
  neverSynced:'The device has not reported any shadow state yet.',
  unknown:    'Shadow sync status unavailable.',
};

// Map shadow sync statuses to RealtimeIndicator STATUS_COLORS keys
const SHADOW_STATUS_COLOR_KEY: Record<ShadowSyncStatus, keyof typeof riStyles.STATUS_COLORS> = {
  synced:     'online',
  pending:    'stale',
  neverSynced:'unknown',
  unknown:    'unknown',
};

function ShadowSyncIndicator({ settings, lastShadowMs, isSimulated, domain, onClick }: {
  settings: EnrichedSetting[] | null | undefined;
  lastShadowMs: number | undefined;
  isSimulated?: boolean;
  domain?: string | null;
  onClick: () => void;
}) {
  const { t, tx } = useI18n();
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 10_000);
    return () => clearInterval(id);
  }, []);

  const wrapperRef = React.useRef<HTMLSpanElement>(null);
  const [tooltipVisible, setTooltipVisible] = React.useState(false);
  const [tooltipPos, setTooltipPos] = React.useState<{ bottom: number; left: number } | null>(null);

  const showTooltip = React.useCallback(() => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setTooltipPos({ bottom: window.innerHeight - rect.top + 8, left: rect.left + rect.width / 2 });
    }
    setTooltipVisible(true);
  }, []);
  const hideTooltip = React.useCallback(() => setTooltipVisible(false), []);

  const status = deriveShadowSyncStatus(settings);
  const colors = riStyles.STATUS_COLORS[SHADOW_STATUS_COLOR_KEY[status]];

  const icon = status === 'synced' || status === 'pending'
    ? <svg width={8} height={8} viewBox="0 0 8 8" aria-hidden="true"><circle cx={4} cy={4} r={4} fill={colors.ring} /></svg>
    : <HelpCircle size={11} strokeWidth={2.2} />;

  const relTime = lastShadowMs != null ? formatSyncAge(lastShadowMs) : null;
  const absTime = lastShadowMs != null ? new Date(lastShadowMs).toLocaleString() : null;

  const tooltipEl = tooltipVisible && tooltipPos ? (
    <div
      className={riStyles.tooltipPortal}
      style={{ position: "fixed", bottom: tooltipPos.bottom, left: tooltipPos.left, transform: "translateX(-50%)" }}
      role="tooltip"
    >
      <div className={riStyles.tooltipInner} style={{ position: "relative" }}>
        <span className={riStyles.tooltipTitle}>{SHADOW_STATUS_LABEL[status]}</span>
        {domain && (
          <span className={riStyles.tooltipRow}>Domain: {domain.charAt(0).toUpperCase() + domain.slice(1)}</span>
        )}
        {relTime && absTime && (
          <span className={riStyles.tooltipRow}>Last shadow update: {relTime} ({absTime})</span>
        )}
        <span className={riStyles.tooltipRow}>{tx("pro.productSettings.viewShadowJson")}</span>
        <span className={riStyles.tooltipDescription}>{SHADOW_STATUS_DESC[status]}</span>
        <div className={riStyles.tooltipCaret} />
      </div>
    </div>
  ) : null;

  return (
    <>
      <span
        ref={wrapperRef}
        className={riStyles.badge}
        style={{ background: colors.background, color: colors.color, borderColor: colors.borderColor, cursor: 'pointer' }}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onClick={() => { hideTooltip(); onClick(); }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
        aria-label={`${SHADOW_STATUS_LABEL[status]} — ${t("pro.productSettings.viewShadowJson")}`}
      >
        <span className={riStyles.iconSlot}>{icon}</span>
        {isSimulated ? 'Simulated' : 'IoT Core'}
      </span>
      {typeof document !== "undefined" && ReactDOM.createPortal(tooltipEl, document.body)}
    </>
  );
}

// ─── Shadow JSON modal ────────────────────────────────────────────────────────

interface ShadowJsonModalProps {
  open: boolean;
  onClose: () => void;
  productId: string;
  token: string;
  initialShadow: string;
  shadowNames: string[];
  product: Product;
}

function ShadowJsonModal({ open, onClose, productId, token, initialShadow, shadowNames, product }: ShadowJsonModalProps) {
  const { t, tx } = useI18n();
  const [selectedShadow, setSelectedShadow] = React.useState(initialShadow);
  const [shadowData, setShadowData] = React.useState<ShadowState | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);

  const handleCopy = React.useCallback(() => {
    if (!shadowData) return;
    navigator.clipboard.writeText(JSON.stringify(shadowData.state, null, 2)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [shadowData]);

  // Sync selected shadow to the caller's current selection each time the modal opens
  React.useEffect(() => {
    if (open) setSelectedShadow(initialShadow);
  }, [open, initialShadow]);

  // Fetch shadow data whenever selected shadow changes while open
  React.useEffect(() => {
    if (!open || !token || !selectedShadow) return;
    setLoading(true);
    setError(null);
    setShadowData(null);
    fetchProductShadow(token, productId, selectedShadow)
      .then((res) => setShadowData(res.shadow))
      .catch((err) => setError(err?.message || "Failed to load shadow data"))
      .finally(() => setLoading(false));
  }, [open, token, productId, selectedShadow]);

  return (
    <Modal open={open} setOpen={(v) => { if (!v) onClose(); }} minWidth="620px" maxWidth="860px" maxHeight="80vh">
      <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: "4px 0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Braces size={18} style={{ color: "var(--color-fgMuted)" }} />
          <Text level="h3" weight="bold">{tx("pro.productSettings.shadowJson")}</Text>
        </div>

        {shadowNames.length > 1 && (
          <TabSwitcher
            tabs={shadowNames.map((name) => ({ value: name, label: name.replace(/_settings$/, "").replace(/_/g, " ") }))}
            value={selectedShadow}
            onChange={setSelectedShadow}
          />
        )}

        <div style={{ borderRadius: 8, overflow: "hidden", minHeight: 120 }}>
          {loading && (
            <Text level="body-small" style={{ color: "#9CA3AF", padding: "12px 0" }}>{tx("shared.common.loading")}</Text>
          )}
          {error && <Callout variant="error">{error}</Callout>}
          {!loading && !error && shadowData && (
            <div style={{ borderRadius: 8, padding: "12px 16px", fontSize: 12, lineHeight: 1.6, overflowX: "auto", fontFamily: "monospace" }}>
              <JsonView
                data={shadowData.state}
                style={darkStyles}
                shouldExpandNode={(level: number) => level < 3}
              />
            </div>
          )}
          {!loading && !error && !shadowData && selectedShadow && (
            <Text level="body-small" style={{ color: "#9CA3AF" }}>
              {product.isSimulated && !product.isSimulating
                ? "No shadow state available — start a simulation session to generate device state data."
                : "No shadow data returned for this device."}
            </Text>
          )}
        </div>

        {shadowData && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              iconLeft={copied ? <Check strokeWidth={2.5} /> : <Copy />}
              onClick={handleCopy}
            >
              {copied ? "Copied!" : "Copy JSON"}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function ProductSettingsPage({ productId }: ProductSettingsPageProps) {
  const { t, tx } = useI18n();
  const { accessToken } = useAuth();
  const navigate = useNavigate({ from: "/workspace/products/$productId/settings" });
  const { subscribe, unsubscribe } = useWebSocket();
  const { confirm, ConfirmModal } = useConfirmModal();

  const [product, setProduct] = React.useState<Product | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [shadowNames, setShadowNames] = React.useState<string[]>([]);
  const [selectedShadow, setSelectedShadow] = React.useState<string | null>(null);

  const [settingsResult, setSettingsResult] = React.useState<ShadowPipelineResult | null>(null);
  const settingsResultRef = React.useRef(settingsResult);
  settingsResultRef.current = settingsResult;
  const [settingsLoading, setSettingsLoading] = React.useState(false);
  const [settingsError, setSettingsError] = React.useState<string | null>(null);

  const {
    isExpanded: isSectionExpanded,
    toggle: toggleSection,
    bindSectionLayout,
  } = useSectionCollapse(
    productSettingsCollapseScope(productId, selectedShadow),
  );

  const [localEdits, setLocalEdits] = React.useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  const [saving, setSaving] = React.useState(false);
  const [shadowJsonOpen, setShadowJsonOpen] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const [flashKeys, setFlashKeys] = React.useState<Record<string, number>>({});
  // Track lastDeviceSync per shadow name; take the max as the product-wide last sync.
  const [shadowSyncTimes, setShadowSyncTimes] = React.useState<Record<string, number>>({});
  const deviceLastSync = Object.values(shadowSyncTimes).length > 0
    ? Math.max(...Object.values(shadowSyncTimes))
    : undefined;
  // lastShadowAt from the telemetry heartbeat cache — same source as ProductDetailPage.
  const [lastShadowAtMs, setLastShadowAtMs] = React.useState<number | undefined>(undefined);
  // Track when each setting key was set to pending (keyed by setting key).
  const [pendingSince, setPendingSince] = React.useState<Record<string, number>>({});

  const token = accessToken ?? localStorage.getItem("access_token") ?? "";
  const totalDirty = Object.keys(localEdits).length;
  const hasDirty = totalDirty > 0;

  // Block browser unload when there are unsaved changes
  React.useEffect(() => {
    if (!hasDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasDirty]);

  // Safe navigation wrapper — confirms before leaving with unsaved changes
  const safeNavigate = React.useCallback(async (opts: Parameters<typeof navigate>[0]) => {
    if (hasDirty) {
      const ok = await confirm(
        "Unsaved changes",
        "You have unsaved changes that will be lost if you leave this page.",
        "Leave anyway",
      );
      if (!ok) return;
    }
    navigate(opts);
  }, [hasDirty, confirm, navigate]);

  // Load product and available shadow names (edit-tier required)
  React.useEffect(() => {
    if (!token) return;
    let cancelled = false;

    setLoading(true);
    productAccessApi
      .fetchProductAccessCheck(token, productId)
      .then((accessCheck) => {
        if (cancelled) return null;
        const tier = accessCheck.access?.tier;
        const canEdit = tier === "edit" || tier === "full";
        if (!accessCheck.allowed || !canEdit) {
          navigate({ to: "/workspace/products/$productId", params: { productId }, replace: true });
          return null;
        }
        return getProductById(token, productId);
      })
      .then((p) => {
        if (cancelled || !p) return;
        setProduct(p);
        if (isBluetoothOnlyLine(p.productModel?.productLine)) return;
        return fetchProductSettingShadowNames(token, productId).then((names) => {
          if (cancelled) return;
          const sorted = [...names].sort((a, b) => {
            if (a === "settings") return -1;
            if (b === "settings") return 1;
            return a.localeCompare(b);
          });
          setShadowNames(sorted);
          if (sorted.length > 0) setSelectedShadow(sorted[0]!);
        }).catch(() => {});
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to load product");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [token, productId, navigate]);

  // Fetch lastShadowAt from the telemetry heartbeat cache (same source as ProductDetailPage)
  React.useEffect(() => {
    if (!token) return;
    fetchTelemetryLatest(token, productId)
      .then((snap) => {
        if (snap.lastShadowAt) setLastShadowAtMs(new Date(snap.lastShadowAt).getTime());
      })
      .catch(() => {});
  }, [token, productId]);

  // Load settings when selected shadow changes
  React.useEffect(() => {
    if (!token || !selectedShadow) return;
    let cancelled = false;

    setSettingsLoading(true);
    setSettingsError(null);
    setLocalEdits({});
    setFieldErrors({});

    fetchProductShadowSettings(token, productId, selectedShadow)
      .then((result) => {
        if (cancelled) return;
        setSettingsResult(result);
        if (result.lastDeviceSync != null) {
          setShadowSyncTimes((prev) => ({ ...prev, [selectedShadow]: result.lastDeviceSync! }));
        }
      })
      .catch((err) => { if (!cancelled) setSettingsError(err.message || "Failed to load settings"); })
      .finally(() => { if (!cancelled) setSettingsLoading(false); });

    return () => { cancelled = true; };
  }, [token, productId, selectedShadow]);

  // WebSocket subscription — real-time shadow updates
  React.useEffect(() => {
    const topic = `entity:products:${productId}`;

    const handler = (msg: { payload?: unknown }) => {
      const payload = msg.payload as {
        type?: string;
        shadowName?: string;
        settings?: EnrichedSetting[];
        unconfirmedKeys?: string[];
        lastDeviceSync?: number;
      } | null;

      if (!payload) return;

      if (payload.type === "shadow.settings" && payload.shadowName === selectedShadow && payload.settings) {
        if (payload.lastDeviceSync != null && payload.shadowName) {
          setShadowSyncTimes((prev) => ({ ...prev, [payload.shadowName!]: payload.lastDeviceSync! }));
        }
        // Determine which keys actually changed value before overwriting state
        const prevSettings = settingsResultRef.current?.settings ?? [];
        const changedKeys = new Set(
          payload.settings
            .filter((s) => s.status === "synced")
            .filter((s) => {
              const prev = prevSettings.find((p) => p.key === s.key);
              return prev?.reportedValue !== s.reportedValue;
            })
            .map((s) => s.key),
        );
        setSettingsResult((prev) => prev ? { ...prev, settings: payload.settings! } : null);
        if (changedKeys.size > 0) {
          setFlashKeys((prev) => {
            const next = { ...prev };
            changedKeys.forEach((key) => { next[key] = (prev[key] ?? 0) + 1; });
            return next;
          });
        }
        setLocalEdits((prev) => {
          const next = { ...prev };
          (payload.settings ?? []).forEach((setting) => {
            if (setting.status === "synced") delete next[setting.key];
          });
          return next;
        });
        setFieldErrors((prev) => {
          const next = { ...prev };
          (payload.settings ?? []).forEach((setting) => {
            if (setting.status === "synced") delete next[setting.key];
          });
          return next;
        });
        // Clear pendingSince for any keys that came back as synced
        setPendingSince((prev) => {
          const next = { ...prev };
          (payload.settings ?? []).forEach((s) => { if (s.status === "synced") delete next[s.key]; });
          return next;
        });
      } else if (payload.type === "shadow.updated" && payload.shadowName === selectedShadow) {
        fetchProductShadowSettings(token, productId, selectedShadow)
          .then((result) => {
            setSettingsResult(result);
            setPendingSince((prev) => {
              const next = { ...prev };
              result.settings.forEach((s) => { if (s.status === "synced") delete next[s.key]; });
              return next;
            });
          })
          .catch(() => {});
      }
    };

    subscribe([topic], handler);
    return () => unsubscribe([topic], handler);
  }, [productId, selectedShadow, token, subscribe, unsubscribe]);

  // Poll settings while any are pending so the UI catches up when IoT Core clears
  // a delta without a device ack (e.g. revert while device is offline).
  const hasPendingSettings = (settingsResult?.settings ?? []).some(
    (s) => s.status === "pending" || s.status === "unconfirmed",
  );
  // Use a ref so the interval callback always sees the current value without
  // the effect needing to restart every time hasPendingSettings changes.
  const hasPendingRef = React.useRef(hasPendingSettings);
  hasPendingRef.current = hasPendingSettings;

  React.useEffect(() => {
    if (!token || !selectedShadow) return;
    const interval = setInterval(() => {
      if (!hasPendingRef.current) return;
      fetchProductShadowSettings(token, productId, selectedShadow)
        .then((result) => {
          setSettingsResult(result);
          if (result.lastDeviceSync != null) {
            setShadowSyncTimes((prev) => ({ ...prev, [selectedShadow]: result.lastDeviceSync! }));
          }
          setPendingSince((prev) => {
            const next = { ...prev };
            result.settings.forEach((s) => { if (s.status === "synced") delete next[s.key]; });
            return next;
          });
        })
        .catch(() => {});
    }, 8_000);
    return () => clearInterval(interval);
  }, [token, productId, selectedShadow]);

  const handleLocalChange = React.useCallback(
    (key: string, value: string, reportedValue?: string | null, forceSet?: boolean) => {
      setFieldErrors((prev) => {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      // forceSet keeps the local edit even when value === reportedValue — used when
      // reverting a pending field so the save explicitly clears the IoT Core delta.
      if (!forceSet && value === (reportedValue ?? "")) {
        setLocalEdits((prev) => {
          if (!(key in prev)) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
      } else {
        setLocalEdits((prev) => ({ ...prev, [key]: value }));
      }
    },
    [],
  );

  async function handleSaveAll() {
    if (!selectedShadow || !hasDirty) return;

    // Reverse-scale display values back to raw IoT integers before writing.
    // EnrichedSetting.rawScale is the forward multiplier (raw × rawScale = display),
    // so the inverse is: rawValue = displayValue / rawScale.
    const settingMap = new Map((settingsResult?.settings ?? []).map((s) => [s.key, s]));
    // Snapshot display values now — these go into the optimistic UI (scaled for display).
    // The datum below is reverse-scaled for IoT Core and must NOT be used for display.
    const displayValues = { ...localEdits };

    const datum: Record<string, string> = {};
    for (const [key, displayValue] of Object.entries(localEdits)) {
      const setting = settingMap.get(key);
      const rawScale = setting?.rawScale;
      if (rawScale != null && rawScale !== 0) {
        const raw = parseFloat(displayValue) / rawScale;
        datum[key] = String(Math.round(raw));
      } else {
        datum[key] = displayValue;
      }
    }

    setSettingsResult((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        settings: prev.settings.map((s) =>
          s.key in datum ? { ...s, status: "pending" as const, desiredValue: displayValues[s.key] } : s
        ),
      };
    });

    const now = Date.now();
    setPendingSince((prev) => {
      const next = { ...prev };
      Object.keys(datum).forEach((k) => { if (!(k in next)) next[k] = now; });
      return next;
    });

    setSaving(true);
    setSaveError(null);
    try {
      await updateProductShadowSettings(token, productId, selectedShadow!, datum);
      setLocalEdits((prev) => {
        const next = { ...prev };
        Object.keys(datum).forEach((k) => delete next[k]);
        return next;
      });
    } catch (err: unknown) {
      const msg = (err as Error).message || "Failed to save settings";
      setSaveError(msg);
      setSettingsResult((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          settings: prev.settings.map((s) =>
            s.key in datum ? { ...s, status: "synced" as const, desiredValue: undefined } : s
          ),
        };
      });
      setFieldErrors((prev) => {
        const next = { ...prev };
        Object.keys(datum).forEach((k) => { next[k] = msg; });
        return next;
      });
    } finally {
      setSaving(false);
    }
  }

  // Group settings by section
  const settingsBySection = React.useMemo((): Array<{ section: string; settings: EnrichedSetting[] }> => {
    if (!settingsResult) return [];
    const map = new Map<string, EnrichedSetting[]>();
    for (const setting of settingsResult.settings) {
      const key = setting.section ?? "";
      const list = map.get(key) ?? [];
      list.push(setting);
      map.set(key, list);
    }
    return Array.from(map.entries()).map(([section, settings]) => ({ section, settings }));
  }, [settingsResult]);

  const sectionNavItems = React.useMemo(
    () =>
      settingsBySection.map(({ section }) => ({
        id: sectionToId(section),
        label: section || "General",
        icon: sectionIcon(section, 14),
      })),
    [settingsBySection],
  );

  const sectionLayout = bindSectionLayout(sectionNavItems);

  const changeLines = React.useMemo(() => {
    const settings = settingsResult?.settings ?? [];
    const settingMap = new Map(settings.map((s) => [s.key, s]));
    return Object.entries(localEdits).map(([key, value]) => {
      const setting = settingMap.get(key);
      const label = setting?.label ?? key;
      let displayValue = value;
      if (setting?.valueType === "boolean") {
        displayValue =
          value === "1" ? t("pro.productSettings.on") : t("pro.productSettings.off");
      } else if (setting?.valueType === "enum" && setting.enumOptions) {
        displayValue =
          setting.enumOptions.find((o) => o.value === value)?.label ?? value;
      } else if (setting?.unit) {
        displayValue = `${value} ${setting.unit}`;
      }
      return `${label}: ${displayValue}`;
    });
  }, [localEdits, settingsResult?.settings, t]);

  const floatingActions = React.useMemo((): FloatingAction[] => {
    if (!hasDirty) return [];
    return [
      {
        id: "reset",
        icon: <RotateCcw size={16} />,
        shape: "circle",
        tooltip: t("pro.productSettings.resetUnsaved"),
        disabled: saving,
        onClick: () => {
          setLocalEdits({});
          setFieldErrors({});
        },
      },
      {
        id: "save",
        label:
          totalDirty === 1
            ? `Save ${totalDirty} change`
            : `Save ${totalDirty} changes`,
        icon: <Save size={16} />,
        shape: "rectangle",
        loading: saving,
        disabled: saving,
        tooltip:
          changeLines.length > 0 ? (
            <div>
              {changeLines.map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
          ) : undefined,
        onClick: handleSaveAll,
      },
    ];
  }, [hasDirty, totalDirty, saving, changeLines, t, handleSaveAll]);

  if (loading) {
    return (
      <div className={s.page}>
        <div className={s.pageColumn}>
          <Text level="body-medium" style={{ color: "#9CA3AF" }}>{tx("shared.common.loading")}</Text>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className={s.page}>
        <div className={s.pageColumn}>
          <button className={s.backLink} onClick={() => safeNavigate({ to: "/workspace/products/$productId" })}>
            <ArrowLeft size={14} /> Back to Product
          </button>
          <Callout variant="error">{error ?? "Product not found."}</Callout>
        </div>
      </div>
    );
  }

  if (isBluetoothOnlyLine(product.productModel?.productLine)) {
    return (
      <DetailPageLayout
        backLink={{
          label: product.name,
          onClick: () => safeNavigate({ to: "/workspace/products/$productId" }),
        }}
        header={{
          media: <Settings size={28} color="#9CA3AF" />,
          title: tx("pro.productSettings.settings"),
          subtitle: product.name,
        }}
      >
        <ComingSoonCard title={t("pro.products.bluetoothOnly.title")} message={t("pro.products.bluetoothOnly.settingsMessage")} />
      </DetailPageLayout>
    );
  }

  return (
    <>
      <DetailPageLayout
        backLink={{
          label: product.name,
          onClick: () => safeNavigate({ to: "/workspace/products/$productId" }),
        }}
        header={{
          media: <Settings size={28} color="#9CA3AF" />,
          title: tx("pro.productSettings.settings"),
          subtitle: product.name,
        }}
        tabs={
          shadowNames.length > 1 ? (
            <TabSwitcher
              tabs={shadowNames.map((name) => ({
                value: name,
                label: name.replace(/_settings$/, "").replace(/_/g, " "),
              }))}
              value={selectedShadow ?? ""}
              onChange={setSelectedShadow}
            />
          ) : undefined
        }
        statusIndicators={[
          <ShadowSyncIndicator
            key="sync"
            settings={settingsResult?.settings}
            lastShadowMs={lastShadowAtMs ?? deviceLastSync}
            isSimulated={product?.isSimulated}
            domain={product?.domain}
            onClick={() => setShadowJsonOpen(true)}
          />,
        ]}
        {...sectionLayout}
        floatingActions={floatingActions}
      >
        {shadowNames.length === 0 && !settingsLoading && (
          <Callout variant="info">{tx("pro.productSettings.noShadows")}</Callout>
        )}

        {saveError && (
          <div style={{ marginBottom: 16 }}>
            <Callout variant="error">{saveError}</Callout>
          </div>
        )}

        {settingsLoading && (
          <Text
            level="body-small"
            style={{ color: "#9CA3AF", marginBottom: 16 }}
          >
            {tx("pro.productSettings.loadingSettings")}
          </Text>
        )}

        {settingsError && <Callout variant="error">{settingsError}</Callout>}

        {!settingsLoading &&
          settingsResult &&
          settingsResult.settings.length === 0 && (
            <div className={s.emptyState}>
              {tx("pro.productSettings.noSettingsFound")}
            </div>
          )}

        {!settingsLoading &&
          settingsBySection.map(({ section, settings }) => (
            <SettingSection
              key={section}
              sectionName={section}
              settings={settings}
              localEdits={localEdits}
              fieldErrors={fieldErrors}
              flashKeys={flashKeys}
              onLocalChange={handleLocalChange}
              deviceLastSync={deviceLastSync}
              pendingSince={pendingSince}
              expanded={isSectionExpanded(sectionToId(section))}
              onToggleExpanded={() => toggleSection(sectionToId(section))}
            />
          ))}
      </DetailPageLayout>

      {product && (
        <ShadowJsonModal
          open={shadowJsonOpen}
          onClose={() => setShadowJsonOpen(false)}
          productId={productId}
          token={token}
          initialShadow={selectedShadow ?? ""}
          shadowNames={shadowNames}
          product={product}
        />
      )}

      <ConfirmModal />
    </>
  );
}
